<template>
  <div class="getnet-credentials">
    <p class="text-muted mb-3">
      Credenciais usadas nos links de pagamento Getnet desta unidade.
    </p>

    <div v-if="loading" class="text-muted">Carregando credenciais...</div>

    <base-alert v-else-if="forbidden" type="danger" class="w-100">
      <i class="fa fa-exclamation-triangle"></i>
      Você não tem permissão para gerenciar as credenciais desta unidade.
    </base-alert>

    <base-alert v-else-if="loadError" type="danger" class="w-100">
      <i class="fa fa-exclamation-triangle"></i>
      Não foi possível carregar as credenciais.
      <template v-if="loadErrorMessage">{{ loadErrorMessage }}</template>
      <a href="#" class="alert-link ml-2" @click.prevent="load">Tentar novamente</a>
    </base-alert>

    <template v-else>
      <base-alert v-if="formato === null" type="info" class="w-100">
        <i class="fa fa-info-circle"></i>
        Nenhuma credencial própria cadastrada. Os links de pagamento desta
        unidade usam a conta padrão da Janoo até que você cadastre as suas.
      </base-alert>

      <base-alert v-if="formato === 'legado'" type="warning" class="w-100">
        <i class="fa fa-exclamation-triangle"></i>
        Estas credenciais estão em um formato antigo. Ao salvar qualquer campo
        elas serão migradas automaticamente.
      </base-alert>

      <form autocomplete="off" @submit.prevent="save">
        <div class="row">
          <div v-for="campo in campos" :key="campo.chave" class="col-md-6 mb-3">
            <label class="form-control-label d-block">
              {{ campo.public_label }}
            </label>

            <div v-if="editing[campo.chave]" class="input-group">
              <input
                :id="`getnet-${campo.chave}`"
                v-model="draft[campo.chave]"
                type="password"
                class="form-control"
                autocomplete="new-password"
                spellcheck="false"
                :placeholder="`Novo ${campo.public_label}`"
                :disabled="saving"
              />
              <div class="input-group-append">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  title="Cancelar edição"
                  :disabled="saving"
                  @click="cancelEdit(campo.chave)"
                >
                  <i class="fa fa-times"></i>
                </button>
              </div>
            </div>

            <div v-else class="input-group">
              <input
                :id="`getnet-${campo.chave}`"
                type="text"
                class="form-control"
                :value="campo.preenchido ? campo.preview : ''"
                placeholder="Não cadastrado"
                readonly
              />
              <div class="input-group-append">
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  title="Editar"
                  :disabled="saving"
                  @click="startEdit(campo.chave)"
                >
                  <i class="fa fa-pencil-alt"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <small class="form-text text-muted mb-3">
          Por segurança, as chaves são exibidas mascaradas e não podem ser
          visualizadas após o cadastro. Para trocar, informe um novo valor.
        </small>

        <div class="d-flex align-items-center">
          <b-button
            type="submit"
            variant="primary"
            :disabled="!hasPendingEdits || saving"
          >
            {{ saving ? 'Salvando...' : 'Salvar' }}
          </b-button>

          <b-button
            v-if="formato !== null"
            type="button"
            variant="outline-danger"
            class="ml-auto"
            :disabled="saving"
            @click="removeAll"
          >
            Remover credenciais
          </b-button>
        </div>
      </form>
    </template>
  </div>
</template>

<script>
import swal from 'sweetalert2'
import nodeService from '@/services/nodejs'

const emptyDraft = () => ({})

// nodeApi3 rejeita com o próprio response ({ status, data }); sem response
// (timeout/rede) rejeita com o erro do axios
const apiError = (error) => {
  const response = error && error.status ? error : error && error.response
  return {
    status: response && response.status,
    data: response && response.data,
  }
}
const emptyEditing = () => ({})

export default {
  name: 'GetnetCredentialsTab',

  props: {
    placeUuid: { type: String, default: '' },
  },

  data() {
    return {
      loading: false,
      loaded: false,
      saving: false,
      forbidden: false,
      loadError: false,
      loadErrorMessage: '',
      formato: null,
      campos: [],
      editing: emptyEditing(),
      draft: emptyDraft(),
    }
  },

  computed: {
    hasPendingEdits() {
      return Object.keys(this.editing).some((chave) => this.editing[chave])
    },
  },

  mounted() {
    // A aba e renderizada com `lazy`: montar = operador abriu a aba.
    this.activate()
  },

  watch: {
    placeUuid() {
      this.loaded = false
      this.load()
    },
  },

  methods: {
    /** Chamado pela aba ao ser ativada; carrega uma vez por unidade. */
    activate() {
      if (!this.loaded && !this.loading) this.load()
    },

    applyView(view) {
      this.formato = view && view.formato !== undefined ? view.formato : null
      this.campos = Array.isArray(view && view.campos) ? view.campos : []
      this.editing = emptyEditing()
      this.draft = emptyDraft()
    },

    async load() {
      if (!this.placeUuid) return
      this.loading = true
      this.forbidden = false
      this.loadError = false
      this.loadErrorMessage = ''
      try {
        const response = await nodeService.getPlaceGetnetCredentials(this.placeUuid)
        this.applyView(response && response.data)
        this.loaded = true
      } catch (error) {
        const { status, data } = apiError(error)
        if (status === 403) this.forbidden = true
        else {
          this.loadError = true
          this.loadErrorMessage = (data && data.message) || ''
        }
      } finally {
        this.loading = false
      }
    },

    startEdit(chave) {
      this.$set(this.editing, chave, true)
      this.$set(this.draft, chave, '')
      this.$nextTick(() => {
        const input = document.getElementById(`getnet-${chave}`)
        if (input) input.focus()
      })
    },

    cancelEdit(chave) {
      this.$delete(this.editing, chave)
      this.$delete(this.draft, chave)
    },

    campoPorChave(chave) {
      return this.campos.find((campo) => campo.chave === chave) || { public_label: chave }
    },

    async save() {
      if (this.saving || !this.hasPendingEdits) return

      const body = {}
      const clearing = []
      Object.keys(this.editing).forEach((chave) => {
        if (!this.editing[chave]) return
        const value = String(this.draft[chave] || '').trim()
        body[chave] = value
        if (!value) clearing.push(this.campoPorChave(chave).public_label)
      })

      if (clearing.length) {
        const answer = await swal.fire({
          title: 'Remover credencial?',
          text: `Isso removerá ${clearing.join(', ')} cadastrado(s). Continuar?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Remover',
          cancelButtonText: 'Cancelar',
        })
        if (!answer.isConfirmed) return
      }

      this.saving = true
      try {
        const response = await nodeService.savePlaceGetnetCredentials(this.placeUuid, body)
        // applyView limpa edição e rascunho: só no sucesso. Na falha o valor
        // digitado fica, senão o campo seguia em edição vazio e um novo
        // "Salvar" mandaria "" (apaga a credencial).
        this.applyView(response && response.data)
        this.$notify({ message: 'Credenciais Getnet salvas.', type: 'success', duration: 4000 })
      } catch (error) {
        this.$notify({
          message: this.errorMessage(error, 'Não foi possível salvar as credenciais.'),
          type: 'danger',
          duration: 5000,
        })
      } finally {
        this.saving = false
      }
    },

    async removeAll() {
      const answer = await swal.fire({
        title: 'Remover todas as credenciais?',
        text: 'Os links de pagamento desta unidade passarão a usar a conta padrão da Janoo.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remover',
        cancelButtonText: 'Cancelar',
      })
      if (!answer.isConfirmed) return

      this.saving = true
      try {
        await nodeService.deletePlaceGetnetCredentials(this.placeUuid)
        this.$notify({ message: 'Credenciais Getnet removidas.', type: 'success', duration: 4000 })
        this.loaded = false
        await this.load()
      } catch (error) {
        this.$notify({
          message: this.errorMessage(error, 'Não foi possível remover as credenciais.'),
          type: 'danger',
          duration: 5000,
        })
      } finally {
        this.saving = false
      }
    },

    errorMessage(error, fallback) {
      const { status, data } = apiError(error)
      if (status === 403) return 'Você não tem permissão para gerenciar as credenciais desta unidade.'
      // 400 (validação) e 500 com orientação ("credenciais ilegíveis", "sem chave
      // de criptografia") trazem texto para o operador
      if (data && data.message) return data.message
      return fallback
    },
  },
}
</script>
